import React from 'react';
import {
  Box,
  Chip,
  Typography,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ClearAll as ClearAllIcon,
  FilterAlt as FilterIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const FilterChips = ({ 
  filters, 
  onRemoveFilter, 
  onClearFilters,
  organizations = [],
  networks = [],
  sections = [],
  categories = [],
  types = [],
  subtypes = [],
  ratingLevels = [], // New prop for rating levels
  // assetsData prop is no longer needed here
}) => {
  const theme = useTheme();
  
  // Check if there are any active filters
  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'status') return value.length > 0;
    if (key === 'installDateFrom' || key === 'installDateTo') return value !== null;
    return value !== '';
  });
  
  if (!hasActiveFilters) {
    return null;
  }
  
  // Status labels mapping
  const statusLabels = {
    active: 'Active',
    maintenance: 'Maintenance',
    inactive: 'Inactive',
    decommissioned: 'Decommissioned',
  };
  
  // Helper to get entity name by ID
  // Helper to get entity name by ID using the provided lookup collections
  const getEntityName = (id, collection) => {
    // Ensure collection is an array before searching
    if (!Array.isArray(collection)) {
        return `ID: ${id}`; // Return ID if collection is invalid
    }
    
    const entity = collection.find(item => item.id === id);
    
    // Return name if found, otherwise return the ID itself or a placeholder
    // This helps identify if a lookup item is missing its name in the source data
    return entity?.name || `Unknown (${id})`;
  };

  return (
    <Box 
      sx={{ 
        mb: 2, 
        px: 2, 
        py: 1.5, 
        backgroundColor: alpha(theme.palette.primary.light, 0.08),
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon 
            fontSize="small" 
            sx={{ color: theme.palette.primary.main, mr: 1 }} 
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Active Filters
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<ClearAllIcon fontSize="small" />}
          onClick={onClearFilters}
          sx={{ fontWeight: 500 }}
        >
          Clear All
        </Button>
      </Box>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {/* Organization filter */}
        {filters.organization_id && (
          <Chip
            label={`Organization: ${getEntityName(filters.organization_id, organizations)}`}
            onDelete={() => onRemoveFilter('organization_id')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        
        {/* Network filter */}
        {filters.network_id && (
          <Chip
            label={`Network: ${getEntityName(filters.network_id, networks)}`}
            onDelete={() => onRemoveFilter('network_id')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        
        {/* Section filter */}
        {filters.section_id && (
          <Chip
            label={`Section: ${getEntityName(filters.section_id, sections)}`}
            onDelete={() => onRemoveFilter('section_id')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        
        {/* Region filter */}
        {filters.region && (
          <Chip
            label={`Region: ${filters.region}`}
            onDelete={() => onRemoveFilter('region')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        
        {/* Category filter */}
        {filters.category_id && (
          <Chip
            label={`Category: ${getEntityName(filters.category_id, categories)}`}
            onDelete={() => onRemoveFilter('category_id')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        
        {/* Type filter */}
        {filters.type_id && (
          <Chip
            label={`Type: ${getEntityName(filters.type_id, types)}`}
            onDelete={() => onRemoveFilter('type_id')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        
        {/* Subtype filter */}
        {filters.subtype_id && (
          <Chip
            label={`Subtype: ${getEntityName(filters.subtype_id, subtypes)}`}
            onDelete={() => onRemoveFilter('subtype_id')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        
        {/* Status filters */}
        {filters.status && filters.status.length > 0 && (
          filters.status.map(status => (
            <Chip
              key={status}
              label={`Status: ${statusLabels[status] || status}`}
              onDelete={() => onRemoveFilter('status', status)}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))
        )}
        
        {/* Installation date filters */}
        {filters.installDateFrom && (
          <Chip
            label={`Install From: ${format(new Date(filters.installDateFrom), 'MM/dd/yyyy')}`}
            onDelete={() => onRemoveFilter('installDateFrom')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        
        {filters.installDateTo && (
          <Chip
            label={`Install To: ${format(new Date(filters.installDateTo), 'MM/dd/yyyy')}`}
            onDelete={() => onRemoveFilter('installDateTo')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        
        {/* Rating filter */}
        {filters.rating_level_id && (
          <Chip
            label={`Rating: ${getRatingLevelName(filters.rating_level_id)}`}
            onDelete={() => onRemoveFilter('rating_level_id')}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>
    </Box>
  );
  
  // Helper function to get rating level name
  function getRatingLevelName(ratingLevelId) {
    if (ratingLevelId === 'null' || ratingLevelId === null) {
      return 'Not Rated';
    }
    
    const ratingLevel = ratingLevels.find(level => level.id === parseInt(ratingLevelId));
    return ratingLevel?.name || `Unknown (${ratingLevelId})`;
  }
};

export default FilterChips; 